import { apiRequest, api } from "./api";
import type { FoodNutrientBasis } from "./nutrition-form-utils";

// Runtime nutrient catalog row — one entry per `nutrition_nutrient` DB row.
// Returned by GET /nutrition/nutrients/ and consumed via useNutrientCatalog.
export type NutrientGroup = "GEN" | "CARB" | "LIP" | "PROT" | "VIT" | "MIN";

export interface NutrientCatalogItem {
  id: number;
  slug: string;
  name: string;
  unit: string;
  group: NutrientGroup;
  order: number;
  is_main: boolean;
  has_rda: boolean;
}

// Type definitions for tag_info nested object
export interface TagInfo {
  texture?: string;
  cuisine_tag_ids?: number[];
  feeding_category_ids?: number[];
  meal_type_ids?: number[];
  condition_tag_ids?: number[];
  allergen_tag_ids?: number[];
  age_suitability_tag_ids?: number[];
  context_tag_ids?: number[];
  source_confidence?: string;
}

// Create-only nested serving on POST /nutrition/food-items/. Required when
// nutrient_basis is "SERV" and rejected when it is "100G" — the food and its
// first serving are created in one transaction (columbus#406), which replaces
// the old create-food-then-add-serving sequence.
export interface FoodItemServingPayload {
  serving_unit: number;
  // Read off the printed label, never inferred from the unit's name: a panel
  // printed "per 2 tablets" makes one tablet 0.5.
  multiplier: string;
  // Explicitly null when the label states no gram figure — a capsule has no
  // meaningful weight, and null says that rather than leaving it to a default.
  weight_g: string | null;
}

export interface FoodItemPayload {
  name: string;
  food_code?: string;
  hindi_name?: string;
  full_name?: string;
  food_type?: string;
  // Create-only, and only when food_type is "S". The API rejects it on other
  // food types, and rejects it on update at any type — see FoodNutrientBasis.
  nutrient_basis?: FoodNutrientBasis;
  // Create-only. Send it exactly when nutrient_basis is "SERV" — the API 400s
  // both a per-serving food without one and a per-100 g food with one.
  serving?: FoodItemServingPayload;
  is_homemade?: boolean;
  is_favorite?: boolean;
  source?: string;
  source_id?: string;
  source_web?: string;
  source_pdf?: string;
  brand?: string;
  energy_kcal?: number;
  water_g?: number;
  nutrient_values?: Record<string, number>;
  tag_info?: TagInfo;
}

export interface FoodItemResponse {
  id: number;
  food_code: string;
  name: string;
  hindi_name: string;
  full_name: string;
  food_type: string;
  nutrient_basis: FoodNutrientBasis;
  // Read-only, derived at publish from whether any ingredient's serving lacks a
  // weight. True → this food may only be logged in whole servings.
  requires_whole_servings: boolean;
  is_homemade: boolean;
  is_favorite: boolean;
  source: string;
  source_code: string;
  source_id: string;
  source_web: string;
  source_pdf: string;
  brand: string;
  brand_code: string;
  entered_by: string;
  entered_date: string | null;
  energy_kcal: string;
  alcohol_g: string;
  caffeine_mg: string;
  water_g: string;
}

export interface FoodSource {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface FoodBrand {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface CreateFoodSourcePayload {
  name: string;
}

export interface CreateFoodBrandPayload {
  name: string;
}

export interface TagItem {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChoiceItem {
  value: string;
  label: string;
}

export interface TagMetadataResponse {
  classification_tags: {
    cuisine_tags: TagItem[];
    feeding_categories: TagItem[];
    meal_types: TagItem[];
    condition_tags: TagItem[];
    allergen_tags: TagItem[];
    age_suitability_tags: TagItem[];
    context_tags: TagItem[];
  };
  choices: {
    texture: ChoiceItem[];
    source_confidence: ChoiceItem[];
  };
  sources_and_brands: {
    food_sources: Array<{
      id: number;
      name: string;
      created_at: string;
      updated_at: string;
    }>;
    food_brands: Array<{
      id: number;
      name: string;
      created_at: string;
      updated_at: string;
    }>;
  };
}

export interface ServingUnit {
  code: number;
  name: string;
}

export interface ServingUnitPayload {
  name: string;
}

export interface ServingUnitResponse {
  code: number;
  name: string;
}

export interface FoodServingListItem {
  id: number;
  food_item_id: number;
  food_item_name: string;
  food_item_code: string;
  serving_unit: ServingUnit;
  serving_unit_code: number;
  serving_unit_name: string;
  description: string;
  weight_g: string;
  multiplier: string;
  is_default: boolean;
  // Present only on the admin list (`include_inactive`) and the switch
  // endpoints. Absent on the plain list, which only ever returns active rows.
  is_active?: boolean;
  // Built-in servings of a recipe food (Full recipe, Serve, gm) follow the
  // recipe and can't be switched off. Absent on the old backend: unlocked.
  is_locked?: boolean;
  locked_reason?: string | null;
}

export interface FoodServingDetail extends FoodServingListItem {}

// What to send depends on the parent food's nutrient_basis:
//   "100G" — weight_g is required (an explicit null is a 400); the multiplier
//            is derived from it server-side and a sent value is ignored. The
//            console sends neither a computed nor a typed multiplier here.
//   "SERV" — multiplier is required on create (decimal(7,4), min 0.0001) with
//            no server default; weight_g is optional, since a capsule has no
//            meaningful gram weight.
export interface FoodServingPayload {
  food_item: number;
  serving_unit: number;
  description?: string;
  weight_g?: string;
  multiplier?: string;
  is_default?: boolean;
}

export interface FoodServingResponse {
  id: number;
  food_item: number;
  serving_unit: number;
  description: string;
  weight_g: string;
  multiplier: string;
  is_default: boolean;
}

export interface FoodServingListParams {
  search?: string;
  is_default?: boolean;
  serving_unit_name?: string;
  food_item_source?: string;
  food_item?: number;
  // Admin only: also list servings that are switched off (403 for anyone else).
  include_inactive?: boolean;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedFoodServingResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FoodServingListItem[];
}

// Nutrition API functions
export const nutritionAPI = {
  // Food Items endpoints
  foodItems: {
    // POST /nutrition/food-items/ - Create food item
    create: async (data: FoodItemPayload): Promise<FoodItemResponse> => {
      return apiRequest(() => api.post("/nutrition/food-items/", data));
    },

    // GET /nutrition/food-items/ - List food items
    list: async (params?: {
      search?: string;
      page?: number;
      page_size?: number;
      appointment_id?: string;
      food_type?: string;
      source?: string;
      has_unpublished_changes?: boolean;
    }): Promise<any> => {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());
      if (params?.appointment_id)
        queryParams.append("appointment_id", params.appointment_id);
      if (params?.food_type) queryParams.append("food_type", params.food_type);
      if (params?.source) queryParams.append("source", params.source);
      if (params?.has_unpublished_changes !== undefined)
        queryParams.append(
          "has_unpublished_changes",
          String(params.has_unpublished_changes)
        );

      return apiRequest(() =>
        api.get(`/nutrition/food-items/?${queryParams.toString()}`)
      );
    },

    // GET /nutrition/food-items/{id}/ - Get food item details
    get: async (id: number): Promise<any> => {
      return apiRequest(() => api.get(`/nutrition/food-items/${id}/`));
    },

    // PATCH /nutrition/food-items/{id}/ - Update food item
    update: async (
      id: number,
      data: Partial<FoodItemPayload>
    ): Promise<FoodItemResponse> => {
      return apiRequest(() => api.patch(`/nutrition/food-items/${id}/`, data));
    },
  },

  // Food Sources endpoints
  foodSources: {
    // GET /nutrition/food-sources/ - List food sources
    list: async (): Promise<FoodSource[]> => {
      return apiRequest(() => api.get("/nutrition/food-sources/"));
    },

    // POST /nutrition/food-sources/ - Create food source
    create: async (data: CreateFoodSourcePayload): Promise<FoodSource> => {
      return apiRequest(() => api.post("/nutrition/food-sources/", data));
    },
  },

  // Food Brands endpoints
  foodBrands: {
    // GET /nutrition/food-brands/ - List food brands
    list: async (): Promise<FoodBrand[]> => {
      return apiRequest(() => api.get("/nutrition/food-brands/"));
    },

    // POST /nutrition/food-brands/ - Create food brand
    create: async (data: CreateFoodBrandPayload): Promise<FoodBrand> => {
      return apiRequest(() => api.post("/nutrition/food-brands/", data));
    },
  },

  // Tag Metadata endpoint
  tagMetadata: {
    // GET /nutrition/tag-metadata/ - Get all tag metadata
    get: async (): Promise<TagMetadataResponse> => {
      return apiRequest(() => api.get("/nutrition/tag-metadata/"));
    },
  },

  // Runtime nutrient catalog — single source of truth for groups, labels,
  // units, and ordering. Mirrors `nutrition_nutrient` rows in the DB.
  nutrients: {
    // GET /nutrition/nutrients/ - Get full nutrient catalog
    list: async (): Promise<NutrientCatalogItem[]> => {
      return apiRequest(() => api.get("/nutrition/nutrients/"));
    },
  },

  // Food Servings endpoints
  foodServings: {
    // GET /nutrition/food-servings/ - List food servings
    list: async (
      params?: FoodServingListParams
    ): Promise<PaginatedFoodServingResponse> => {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.is_default !== undefined)
        queryParams.append("is_default", params.is_default.toString());
      if (params?.serving_unit_name)
        queryParams.append("serving_unit_name", params.serving_unit_name);
      if (params?.food_item_source)
        queryParams.append("food_item_source", params.food_item_source);
      if (params?.food_item)
        queryParams.append("food_item", params.food_item.toString());
      if (params?.include_inactive)
        queryParams.append("include_inactive", "true");
      if (params?.ordering) queryParams.append("ordering", params.ordering);
      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.page_size)
        queryParams.append("page_size", params.page_size.toString());

      const queryString = queryParams.toString();
      return apiRequest(() =>
        api.get(
          `/nutrition/food-servings/${queryString ? `?${queryString}` : ""}`
        )
      );
    },

    // GET /nutrition/food-servings/{id}/ - Get food serving details
    get: async (id: number): Promise<FoodServingDetail> => {
      return apiRequest(() => api.get(`/nutrition/food-servings/${id}/`));
    },

    // POST /nutrition/food-servings/{id}/deactivate/ - Switch a serving off.
    // Admin only. Instant: no draft, no If-Match, no new food version. The row
    // is kept, and plans already using it keep it.
    deactivate: async (id: number): Promise<FoodServingDetail> => {
      return apiRequest(() =>
        api.post(`/nutrition/food-servings/${id}/deactivate/`)
      );
    },

    // POST /nutrition/food-servings/{id}/reactivate/ - Switch a serving back on.
    // Admin only. It returns as an ordinary serving, never the default.
    reactivate: async (id: number): Promise<FoodServingDetail> => {
      return apiRequest(() =>
        api.post(`/nutrition/food-servings/${id}/reactivate/`)
      );
    },

    // POST /nutrition/food-servings/add/ - Add a serving to a food at once.
    // Any console user. Instant: no draft, no If-Match, no new food version. A
    // new serving is never the default, and the food keeps its draft untouched.
    add: async (data: FoodServingPayload): Promise<FoodServingDetail> => {
      return apiRequest(() => api.post("/nutrition/food-servings/add/", data));
    },

    // POST /nutrition/food-servings/ - Create food serving
    create: async (data: FoodServingPayload): Promise<FoodServingResponse> => {
      return apiRequest(() => api.post("/nutrition/food-servings/", data));
    },

    // PATCH /nutrition/food-servings/{id}/ - Update food serving
    update: async (
      id: number,
      data: Partial<FoodServingPayload>
    ): Promise<FoodServingResponse> => {
      return apiRequest(() =>
        api.patch(`/nutrition/food-servings/${id}/`, data)
      );
    },
  },

  // Serving Units endpoints
  servingUnits: {
    // GET /nutrition/serving-units/ - List serving units
    list: async (params?: {
      search?: string;
      ordering?: string;
    }): Promise<ServingUnit[]> => {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append("search", params.search);
      if (params?.ordering) queryParams.append("ordering", params.ordering);

      const queryString = queryParams.toString();
      return apiRequest(() =>
        api.get(
          `/nutrition/serving-units/${queryString ? `?${queryString}` : ""}`
        )
      );
    },

    // GET /nutrition/serving-units/<code>/ - Get serving unit details
    get: async (code: number): Promise<ServingUnitResponse> => {
      return apiRequest(() => api.get(`/nutrition/serving-units/${code}/`));
    },

    // POST /nutrition/serving-units/ - Create serving unit
    create: async (data: ServingUnitPayload): Promise<ServingUnitResponse> => {
      return apiRequest(() => api.post("/nutrition/serving-units/", data));
    },

    // PATCH /nutrition/serving-units/<code>/ - Update serving unit
    update: async (
      code: number,
      data: Partial<ServingUnitPayload>
    ): Promise<ServingUnitResponse> => {
      return apiRequest(() =>
        api.patch(`/nutrition/serving-units/${code}/`, data)
      );
    },
  },
};
